/**
 * Cabinet — Partner Brands view (Phase 3.6).
 *
 * Renamed from "Catalogue" per item #30. Lists the partner brands a
 * customer can request products from, grouped by category, each with its
 * name and a link to the brand's official website.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { useI18n, localized } from "@/cabinet/i18n";
import PageHeader from "@/cabinet/components/layout/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORY_LABELS = {
  food:     { en: "Food",      ka: "საკვები" },
  hygiene:  { en: "Hygiene",   ka: "ჰიგიენა" },
  vitamins: { en: "Vitamins",  ka: "ვიტამინები" },
  other:    { en: "Other",     ka: "სხვა" },
};

export default function CataloguePage() {
  const { t, lang } = useI18n();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/partner-brands`);
        const data = await res.json();
        if (!cancelled) setBrands(data.items || []);
      } catch {
        if (!cancelled) setBrands([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const byCategory = useMemo(() => {
    const groups = {};
    for (const b of brands) {
      const cat = b.category || "other";
      (groups[cat] = groups[cat] || []).push(b);
    }
    return Object.entries(groups);
  }, [brands]);

  return (
    <div data-testid="cabinet-partner-brands-page">
      <PageHeader
        title={t("nav.catalogue")}
        subtitle={
          lang === "ka"
            ? "ჩვენი პარტნიორი ბრენდები — დააწკაპუნე და ეწვიე მათ ვებსაიტებს."
            : "Brands we partner with — click to visit their official websites."
        }
        testIdPrefix="cabinet-partner-brands"
      />

      {loading ? (
        <Card className="bg-paper" data-testid="cabinet-partner-brands-loading">
          <CardContent className="flex items-center justify-center py-16 text-[hsl(var(--muted-foreground))]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </CardContent>
        </Card>
      ) : brands.length === 0 ? (
        <Card className="border-dashed bg-paper" data-testid="cabinet-partner-brands-empty">
          <CardContent className="py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">
            {lang === "ka" ? "ჯერ არ გვაქვს ბრენდები." : "No partner brands listed yet."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-10">
          {byCategory.map(([cat, list]) => (
            <section key={cat} data-testid={`cabinet-partner-brands-cat-${cat}`}>
              <h2 className="font-display text-xl font-bold text-[hsl(var(--foreground))] tracking-tight mb-3">
                {localized(CATEGORY_LABELS[cat] || { en: cat, ka: cat }, lang)}
                <span className="ml-2 text-xs font-normal text-[hsl(var(--muted-foreground))]">
                  ({list.length})
                </span>
              </h2>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((b) => {
                  const name = lang === "ka" ? (b.name_ka || b.name) : b.name;
                  const isLink = !!b.website_url;
                  return (
                    <li key={b.id} data-testid={`cabinet-brand-${b.id}`}>
                      <a
                        href={isLink ? b.website_url : undefined}
                        target={isLink ? "_blank" : undefined}
                        rel={isLink ? "noreferrer" : undefined}
                        className={`group flex h-full items-center gap-3 rounded-2xl border bg-paper p-4 transition-all ${
                          isLink
                            ? "hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
                            : "cursor-default"
                        }`}
                      >
                        <span
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-extrabold text-white"
                          style={{ background: b.accent || "#0A4D8C" }}
                        >
                          {name.slice(0, 2).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-bold text-[hsl(var(--foreground))]">{name}</p>
                          {(b.pet_types || []).length > 0 && (
                            <p className="truncate text-xs text-[hsl(var(--muted-foreground))]">
                              {(b.pet_types || []).join(", ")}
                            </p>
                          )}
                        </div>
                        {isLink && (
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--border))] text-[#0A4D8C] transition-all group-hover:bg-[#F25C05] group-hover:border-[#F25C05] group-hover:text-white">
                            <ExternalLink size={14} />
                          </span>
                        )}
                        {!b.active && (
                          <Badge variant="secondary" className="rounded-full">
                            inactive
                          </Badge>
                        )}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
